import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export enum KYCStatus {
  NONE = 'none',
  PENDING = 'pending',
  RESUBMISSION_REQUIRED = 'resubmission_required',
  APPROVED_WITH_PARTNER = 'approved_with_partner',
  APPROVED_WITH_PARTNER_PENDING_CONTRACT = 'approved_with_partner_pending_contract',
  APPROVED_IN_CONTRACT = 'approved_in_contract',
  FAILED = 'failed',
}

export interface LithiumAccountSlice {
  id: number
  profilePictureUrl: string
  walletAddress: string
  multiWallets?: string[]
  username: string
  joinDate: string
  hasVerifiedEmail: boolean
  emailAddress: string
  twitterHandle: string
  discordHandle: string
  discordHashNumber: string
  telegramHandle: string
  telegramUserId?: string
  instagramHandle: string
  createdLithiumToken?: string
  createdAt?: string
  name: string
  raiseAnnouncements: boolean
  weeklyNewsletter: boolean
  productUpdates: boolean
  vestingUpdates: boolean
  socials?: { [key: string]: string }[]
  projectReferrals?: Array<{
    id: number
    code: string
    referrerId: number
    projectId: number
  }>
  kycSessionId?: string
  kycStatus?: KYCStatus
  kycFailureReasons?: string[]
  metadata?: {
    blockedMissions: Array<{
      taskType: 'twitter_follow'
      blockedAt: string
      blockedUntil: string
    }>
    memecoins?: {
      isPublicTrades: boolean
    }
    isInfluencer?: boolean
    // https://developer.x.com/en/docs/twitter-api/data-dictionary/object-model/user
    twitter?: {
      id: string
      profile_image_url: string
      name: string
      username: string
      verified: boolean
      public_metrics: {
        followers_count: number
        following_count: number
        tweet_count: number
        listed_count: number
      }
    }
    sablierStreams?: {
      streamId: number
      lockerAddress: string
      label: string
    }[]
  }
}

const initialState: LithiumAccountSlice = {
  id: 0,
  profilePictureUrl: '',
  walletAddress: '',
  username: '',
  joinDate: '',
  hasVerifiedEmail: false,
  emailAddress: '',
  twitterHandle: '',
  discordHandle: '',
  discordHashNumber: '',
  telegramHandle: '',
  instagramHandle: '',
  name: '',
  raiseAnnouncements: true,
  weeklyNewsletter: true,
  productUpdates: true,
  vestingUpdates: true,
  projectReferrals: [],
}

export const user = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLithiumAccount: (state, action) => {
      state = action.payload
      return state
    },
    setTelegramHandle: (state, { payload }: PayloadAction<string>) => {
      state.telegramHandle = payload
    },
    setDiscordHandle: (state, { payload }: PayloadAction<string>) => {
      state.discordHandle = payload
    },
    setDiscordHashNumber: (state, { payload }: PayloadAction<string>) => {
      state.discordHashNumber = payload
    },
    setTwitterHandle: (state, { payload }: PayloadAction<string>) => {
      state.twitterHandle = payload
    },
    setProfilePictureUrl: (state, { payload }: PayloadAction<string>) => {
      state.profilePictureUrl = payload
    },
    setInstagramHandle: (state, { payload }: PayloadAction<string>) => {
      state.instagramHandle = payload
    },
    setEmailValues: (
      state,
      {
        payload,
      }: PayloadAction<{ emailAddress: string; hasVerifiedEmail: boolean }>,
    ) => {
      state.emailAddress = payload.emailAddress
      state.hasVerifiedEmail = payload.hasVerifiedEmail
    },
  },
})

export const {
  setLithiumAccount,
  setProfilePictureUrl,
  setDiscordHandle,
  setDiscordHashNumber,
  setTelegramHandle,
  setTwitterHandle,
  setInstagramHandle,
  setEmailValues,
} = user.actions

export default user.reducer
